import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, Empty, Space, Row, Col, Spin, Typography, Tag, Collapse, Button, Alert } from "antd";
import { ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useRequestViewContext } from "../../hooks/useRequestView";
import PageHeader from "../../components/common/PageHeader";
import consultaWeb from "../../services/consultaWeb";
import SourceViewer from "../../components/SourceViewer";
import { SourceProvider } from "../../hooks/useSource";

const { Text } = Typography;
const { Panel } = Collapse;

interface RequestData {
  id: string;
  data: any;
  status: string | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
  isPending: boolean;
}

/**
 * Componente para visualizar uma request individual e monitorar seu status.
 */
const RequestViewer: React.FC<{ requestId: string }> = ({ requestId }) => {
  const [requestData, setRequestData] = useState<RequestData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Carrega os dados da request.
   */
  const loadRequest = useCallback(async () => {
    try {
      setLoading(true);
      const response = await consultaWeb.request({ requestId });

      // Verifica se não foi encontrado (404 ou resposta vazia)
      if (!response || !response.success || !response.requested) {
        setRequestData({
          id: requestId,
          data: null,
          status: null,
          loading: false,
          error: "Request não encontrada",
          notFound: true,
          isPending: false,
        });
        // Para o monitoramento se não encontrado
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      const status = response.requested?.status || null;
      const isPending = status === "pending";

      setRequestData({
        id: requestId,
        data: response,
        status,
        loading: false,
        error: null,
        notFound: false,
        isPending,
      });

      // Se ainda está pendente, continua monitorando
      if (isPending) {
        // Se já existe um intervalo, não cria outro
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            loadRequest();
          }, 30000); // 30 segundos
        }
      } else {
        // Se não está mais pendente, para o monitoramento
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch (err: any) {
      console.error("Erro ao buscar request:", err);
      
      // Verifica se é erro 404 (não encontrado)
      const isNotFound = err.response?.status === 404 || err.response?.status === 500;
      
      setRequestData({
        id: requestId,
        data: null,
        status: null,
        loading: false,
        error: err.response?.data?.message || err.message || "Erro ao buscar request",
        notFound: isNotFound,
        isPending: false,
      });
      
      // Para o monitoramento em caso de erro
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadRequest();

    // Cleanup: limpa o intervalo quando o componente é desmontado
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [loadRequest]);

  if (loading && !requestData) {
    return (
      <Collapse>
        <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Text copyable code style={{ fontSize: "14px" }}>
                {requestId}
              </Text>
              <Spin size="small" />
            </div>
          }
          key={requestId}
          disabled
        >
          {/* Conteúdo vazio durante loading */}
        </Panel>
      </Collapse>
    );
  }

  if (!requestData) {
    return null;
  }

  if (requestData.notFound || requestData.error) {
    return (
      <Collapse>
        <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Text copyable code style={{ fontSize: "14px" }}>
                {requestId}
              </Text>
              <Tag color="error" icon={<CloseCircleOutlined />}>
                {requestData.notFound ? "Não encontrada" : "Erro"}
              </Tag>
            </div>
          }
          key={requestId}
          collapsible="disabled"
        >
          {/* Conteúdo intencionalmente vazio: painel não colapsável/não abre */}
        </Panel>
      </Collapse>
    );
  }

  const statusColor = requestData.status === "success" 
    ? "success" 
    : requestData.status === "error" 
    ? "error" 
    : requestData.status === "warning"
    ? "warning"
    : "processing";

  const statusIcon = requestData.status === "success" 
    ? <CheckCircleOutlined /> 
    : requestData.status === "error" 
    ? <CloseCircleOutlined />
    : <ClockCircleOutlined />;

  // Extrair mensagem de warning se existir (ex.: "Sua pesquisa não encontrou nenhum processo disponível.")
  const warningMessage =
    requestData.status === "warning" && requestData.data?.data
      ? typeof requestData.data.data === "string"
        ? requestData.data.data
        : requestData.data.requested?.message || null
      : null;

  // Extrair mensagem de erro quando status é error (exibir em vermelho)
  const errorMessage =
    requestData.status === "error"
      ? (requestData.data?.requested?.message ||
        requestData.data?.requested?.errorMessage ||
        (typeof requestData.data?.data === "string" ? requestData.data.data : null) ||
        requestData.data?.message) ?? null
      : null;

  const requested = requestData.data?.requested;
  return (
    <Collapse>
      <Panel
        header={
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Text copyable code style={{ fontSize: "14px" }}>
              {requestId}
            </Text>
            {requested && (
              <div style={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Tag color="cyan" style={{ fontSize: "12px", marginRight: 4, padding: "0 6px" }}>
                  {requested.platform || "-"}
                </Tag>
                <Tag color="volcano" style={{ fontSize: "12px", marginRight: 4, padding: "0 6px" }}>
                  {requested.search || "-"}
                </Tag>
                <Tag color="green" style={{ fontSize: "12px", marginRight: 4, padding: "0 6px" }}>
                  {requested.query ?? "-"}
                </Tag>
                <Tag color="geekblue" style={{ fontSize: "12px", marginRight: 4, padding: "0 6px" }}>
                  {String(requested.param.key ?? "-")}
                </Tag>
                <Tag color="purple" style={{ fontSize: "12px", marginRight: 4, padding: "0 6px" }}>
                  {String(requested.param.value ?? "-")}
                </Tag>
              </div>
            )}
            <Tag color={statusColor} icon={statusIcon}>
              {requestData.status || "Desconhecido"}
            </Tag>
            {warningMessage && (
              <Text type="warning" style={{ fontSize: "13px" }}>
                {warningMessage}
              </Text>
            )}
            {errorMessage && (
              <Text type="danger" style={{ fontSize: "13px" }}>
                {errorMessage}
              </Text>
            )}
            {requestData.isPending && (
              <Tag color="processing">
                Monitorando...
              </Tag>
            )}
          </div>
        }
        key={requestId}
        collapsible={
          requestData.status === "warning" || requestData.status === "error"
            ? "disabled"
            : undefined
        }
      >
        <SourceViewer sourceId={requestId} />
      </Panel>
    </Collapse>
  );
};

/**
 * Página de monitoramento de requests.
 *
 * Exibe todas as requests que estão sendo monitoradas e atualiza a cada 30 segundos.
 */
const RequestCreateManyMonitoringContent: React.FC = () => {
  const { requestIds } = useRequestViewContext();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate("/request/create-many");
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <PageHeader title="Monitoramento de Requests" />
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            Voltar
          </Button>
        </div>

        <Alert
          message="Monitoramento Ativo"
          description="As requests estão sendo monitoradas a cada 30 segundos. Requests com status 'pending' continuarão sendo monitoradas até que o status mude."
          type="info"
          showIcon
        />

        {requestIds.length === 0 ? (
          <Empty 
            description="Nenhuma request sendo monitorada. Volte para a página de Buscar Várias Capturas para adicionar." 
            image={Empty.PRESENTED_IMAGE_SIMPLE} 
          />
        ) : (
          <Row gutter={[16, 16]}>
            {requestIds.map((requestId, idx) => (
              <Col xs={24} sm={24} md={24} lg={24} xl={24} key={idx}>
                <RequestViewer requestId={requestId} />
              </Col>
            ))}
          </Row>
        )}
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página RequestCreateManyMonitoring.
 */
const RequestCreateManyMonitoring: React.FC = () => {
  return (
    <SourceProvider>
        <RequestCreateManyMonitoringContent />
      </SourceProvider>
  );
};

export default RequestCreateManyMonitoring;
